import React, {
  memo,
  Fragment,
  useMemo,
  Children,
  useState,
  useRef,
  useCallback
} from 'react';
import styled from 'styled-components';
import uniqid from 'uniqid';

import {
  StyledLineProgressionChartWrapper,
  StyledLineProgressionChartSvg,
  StyledProgressBar,
  StyledProgressBarFill,
  StyledProgressBarBackground
} from './LineProgression.styles';
import { useWindowSize } from '../../utils';

const StyledLineProgressionChartText = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: start;
`;

const LineProgression = ({ barHeight = 20, data, color, children }) => {
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);

  const windowSize = useWindowSize();
  const chartId = useRef(uniqid());

  const chartRef = useCallback(
    (node) => {
      if (node != null) {
        const rect = node.getBoundingClientRect();
        setWidth(rect.width);
        setHeight(Math.max(rect.height, barHeight));
      }
    },
    [windowSize, barHeight]
  );

  const innerWidth = width;
  const innerHeight = Math.max(height, barHeight);
  const progressWidth = innerWidth;
  const progressHeight = barHeight;
  const centerY = innerHeight / 2;
  const centerX = innerWidth / 2;

  // Calculate progress percentage and remaining
  const progressPercentage = useMemo(() => {
    if (data === undefined || data === null) return 0;
    return Math.min(Math.max(data * 100, 0), 100);
  }, [data]);

  const chartText = useMemo(() => {
    const texts =
      children &&
      Children.map(children, (child, index) =>
        React.isValidElement(child) ? (
          <Fragment key={`child-${chartId.current}-${index}`}>{child}</Fragment>
        ) : null
      );
    const formattedValue = texts && texts[1] ? texts[1].props.children : '0';
    return (
      <StyledLineProgressionChartText>
        {Number(progressPercentage.toFixed(2))}% - Size:&nbsp;{formattedValue}
      </StyledLineProgressionChartText>
    );
  }, [progressPercentage, children]);

  // Use default dimensions if not available yet
  const displayWidth = width;
  const displayHeight = height;

  return (
    <StyledLineProgressionChartWrapper ref={chartRef}>
      {chartText}
      <StyledLineProgressionChartSvg
        width={displayWidth}
        height={displayHeight}
      >
        <svg width={displayWidth} height={displayHeight}>
          <rect
            rx={14}
            width={displayWidth}
            height={displayHeight}
            fill='#ffffff'
          />
          <g transform={`translate(0, ${centerY - progressHeight / 2})`}>
            {/* Background progress bar */}
            <StyledProgressBarBackground
              width={progressWidth}
              height={progressHeight}
              rx={progressHeight / 2}
              fill='#949494'
              opacity={0.15}
            />

            {/* Progress fill */}
            <StyledProgressBarFill
              width={(progressWidth * progressPercentage) / 100}
              height={progressHeight}
              rx={progressHeight / 2}
              fill={color}
            />
          </g>
        </svg>
      </StyledLineProgressionChartSvg>
    </StyledLineProgressionChartWrapper>
  );
};

LineProgression.displayName = 'LineProgression';

export default memo(LineProgression);
